import React, { ReactElement } from 'react';
import { useParams } from 'react-router-dom';

import LoadingSpinner from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import MessageThreadIndex from '@buyside/components/presentation/MessageThreadIndexPage/MessageThreadIndex/MessageThreadIndex';

import {
  Maybe,
  ThreadMessagesQuery,
  ThreadMessagesDocument,
  ThreadMessagesQueryVariables,
  useCreateMessageForThreadMutation,
  useUpdateIntroRequestStatusMutation,
  useMarkMessageAsReadMutation,
  useThreadMessagesQuery
} from '@interfaces/graphql-typings';

import { truthy } from '@buyside/util/arrayHelper';

const MessagesThreadIndexContainer = (): Maybe<ReactElement> => {
  const { thread } = useParams<{ thread: string }>();
  const threadNum = Number(thread);
  if (!Number.isInteger(threadNum)) throw new Error('Invalid threadNum');

  const { data, loading, previousData, fetchMore, variables } = useThreadMessagesQuery({
    variables: { threadNum },
    fetchPolicy: 'network-only'
  });
  const [createMessageForThread] = useCreateMessageForThreadMutation({
    update(cache, { data }) {
      const newMessage = data?.createMessageForThread?.createdMessage;
      const existingData = cache.readQuery<ThreadMessagesQuery, ThreadMessagesQueryVariables>({
        query: ThreadMessagesDocument,
        variables
      });

      if (newMessage && existingData) {
        cache.writeQuery<ThreadMessagesQuery, ThreadMessagesQueryVariables>({
          query: ThreadMessagesDocument,
          data: {
            threadMessages: {
              edges: [...(existingData.threadMessages?.edges || []), { node: newMessage }],
              pageInfo: existingData.threadMessages?.pageInfo || {
                hasPreviousPage: false,
                startCursor: null
              }
            },
            currentUser: existingData.currentUser
          },
          variables: { threadNum }
        });
      }
    }
  });
  const [markMessageAsRead] = useMarkMessageAsReadMutation();
  const [updateIntroRequest] = useUpdateIntroRequestStatusMutation();

  if (loading && !data && !previousData) return <LoadingSpinner size="medium" />;

  const currentUserId = data?.currentUser?.id;
  const messages = (data || previousData)?.threadMessages?.edges
    ?.map(edge => edge?.node)
    .filter(truthy);
  const pageInfo = data?.threadMessages?.pageInfo;
  if (!currentUserId || !messages || !pageInfo) return null;

  return (
    <MessageThreadIndex
      messages={messages}
      hasPreviousPage={Boolean(pageInfo.hasPreviousPage)}
      fetchPreviousPage={() =>
        fetchMore({
          variables: { ...variables, beforeCursor: pageInfo.startCursor }
        })
      }
      threadNum={threadNum}
      createMessage={createMessageForThread}
      markMessageAsRead={markMessageAsRead}
      updateIntroRequest={updateIntroRequest}
      currentUserId={currentUserId}
    />
  );
};

export default MessagesThreadIndexContainer;
